import * as React from "react";
import { Dialog as DialogPrimitive } from "radix-ui";
import { X } from "lucide-react";

import { cn } from "@/lib/utils";

/**
 * A modal centered over the page — distinct from `Sheet`, which is anchored
 * to an edge. The feedback dialog (#319) is the first thing that wants a
 * centered modal rather than a drawer sliding in from one.
 *
 * Built on Radix's Dialog, the same primitive `Sheet` uses, so the focus
 * trap, the Escape handler, the scroll lock and the return of focus to
 * whatever opened it are the primitive's job rather than ours.
 */
function Dialog({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

function DialogTrigger({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogClose({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        "fixed inset-0 z-50 bg-black/40",
        "data-[state=open]:animate-in data-[state=open]:fade-in-0",
        "data-[state=closed]:animate-out data-[state=closed]:fade-out-0",
        "motion-reduce:animate-none",
        className,
      )}
      {...props}
    />
  );
}

function DialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn(
        "truncate text-base font-semibold tracking-tight",
        className,
      )}
      {...props}
    />
  );
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn("text-muted-foreground truncate text-sm", className)}
      {...props}
    />
  );
}

/**
 * The panel: a header with a name and the way out, then whatever the caller
 * puts inside — this makes no assumption about a footer, so a dialog with
 * its own action row just puts one at the end of `children`.
 *
 * `title` is required rather than optional, the same reasoning `SheetContent`
 * uses: Radix warns without a `Dialog.Title`, and a panel that covers the
 * page owes the reader a name — and a screen reader the same name.
 */
function DialogContent({
  className,
  children,
  title,
  description,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  title: string;
  description?: string;
}) {
  return (
    <DialogPrimitive.Portal>
      <DialogOverlay />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        className={cn(
          "bg-background fixed top-1/2 left-1/2 z-50 flex max-h-[90vh] w-[calc(100%-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 flex-col rounded-lg border shadow-lg",
          "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
          "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
          "duration-200 motion-reduce:animate-none",
          className,
        )}
        {...props}
      >
        <div className="flex shrink-0 items-start justify-between gap-3 border-b px-4 py-3">
          <div className="min-w-0">
            <DialogTitle>{title}</DialogTitle>
            {description ? (
              <DialogDescription>{description}</DialogDescription>
            ) : (
              // Radix warns when a Dialog has no description. Absent one,
              // say so explicitly rather than passing an empty string.
              <DialogDescription className="sr-only">{title}</DialogDescription>
            )}
          </div>

          <DialogPrimitive.Close
            className={cn(
              "text-muted-foreground hover:bg-accent hover:text-foreground",
              "focus-visible:ring-ring shrink-0 rounded-md p-1.5 transition-colors",
              "focus-visible:ring-2 focus-visible:outline-none",
            )}
          >
            <X className="size-4" aria-hidden />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        </div>

        <div className="min-h-0 flex-1 overflow-auto px-4 py-3">{children}</div>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogOverlay,
  DialogTitle,
  DialogTrigger,
};
